import styled from '@emotion/styled';
import { differenceInSeconds } from 'date-fns';
import { Fragment } from 'react';
import { useHover } from 'react-aria';

import { Glyph } from '../common/Glyph';
import { ImageBase } from '../common/ImageBase';
import { TextTiny } from '../common/text/TextTiny';

import { useI18n } from '../../hooks/useI18nFormatters';

import { getIconScaleHover } from '../../theme/functions/getIconScale';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { HomeyImages } from '../../theme/HomeyImages';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

export function NotificationsListItem(props) {
  const { i18n } = useI18n();
  const hover = useHover({});

  const avatarSrc = props.notification.meta?.userAthomId
    ? `${import.meta.env.VITE_ATHOM_CLOUD_API_BASEURL || 'https://api.athom.com'}/user/${
        props.notification.meta.userAthomId
      }/avatar?size=medium`
    : null;

  const date = new Date(props.notification.dateCreated);

  function formatDate(date) {
    if (hover.isHovered) {
      return i18n.dateFormatter.format(date, 'MMM dd yyyy HH:mm:ss');
    }

    if (differenceInSeconds(new Date(), date) <= 60) {
      return i18n.dateFormatter.formatDistanceToNow(date, {
        addSuffix: true,
      });
    }

    return i18n.dateFormatter.formatDistanceToNowStrict(date, {
      addSuffix: true,
    });
  }

  return (
    <TimelineItemContainer {...hover.hoverProps} data-is-hovered={hover.isHovered}>
      {avatarSrc ? (
        <IconMaskContainer>
          <Image round url={avatarSrc} />
        </IconMaskContainer>
      ) : (
        <IconMaskContainer>
          <Glyph
            style={{
              zIndex: 2,
            }}
            size={theme.icon.size_medium}
            color={theme.color.icon_dark}
            source={HomeyImages.getIconForUri(props.notification.ownerUri)}
          />
        </IconMaskContainer>
      )}

      <Content>
        <Excerpt>{mapExcerpt(props.notification?.excerpt ?? '')}</Excerpt>
        <TextTiny lineHeight={1.167} color={theme.color.text_light}>
          {formatDate(date)}
        </TextTiny>
      </Content>
      <DeleteAction
        onClick={() => {
          props.onRequestDelete({ id: props.notification.id });
        }}
      >
        <Glyph size={theme.icon.size_medium} source={glyph.core.crossCircle} />
      </DeleteAction>
    </TimelineItemContainer>
  );
}

function mapExcerpt(excerpt) {
  return excerpt?.split?.('**').map((part, index) => {
    if (index % 2 === 1) return <strong key={index}>{part}</strong>;
    return <Fragment key={index}>{part}</Fragment>;
  });
}

const DeleteAction = styled.div`
  opacity: 0;
  padding: ${su(1)};
  cursor: pointer;

  transition: all ${motion.duration.fast} ${motion.curve.fast_in};

  .${glyphStyles.root} {
    transition: all ${motion.duration.fast} ${motion.curve.fast_in};
    color: ${theme.color.icon_light};
  }

  &:hover {
    .${glyphStyles.root} {
      color: ${theme.color.danger};
      transform: scale(${getIconScaleHover(theme.icon.scale_hover_default)});
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  opacity: ${(props) => (props.isRead ? 0.5 : 1)};
`;

const IconMaskContainer = styled.div`
  display: flex;
  z-index: 2;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  min-width: ${su(6)};
  max-width: ${su(6)};
  min-height: ${su(6)};
  max-height: ${su(6)};
  background-color: ${theme.color.component};
  margin-inline: ${su(1)} ${su(2)};
  border: 2px solid ${theme.color.line};
  border-radius: 50%;
`;

const Image = styled(ImageBase)`
  z-index: 2;
  min-width: 44px;
  max-width: 44px;
  min-height: 44px;
  max-height: 44px;
`;

const Excerpt = styled.div`
  word-break: break-word;
  white-space: pre-wrap;
`;

const TimelineItemContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  min-height: ${su(6)};
  padding: ${su(1)};
  max-width: 380px;
  transition: ${motion.transition.micro};

  &[data-is-hovered='true'] {
    ${DeleteAction} {
      opacity: 1;
    }
  }

  :not(:last-child):after {
    z-index: 1;
    content: '';
    position: absolute;
    top: 58px;
    inset-inline-start: 40px;
    height: 100%;
    overflow: visible;
    width: 2px;
    background-color: ${theme.color.line};
  }
`;
